import { Box, Text } from '@chakra-ui/react'
import React from 'react'
import formatter from 'utils/formatter'
import { initialStateTotalizers } from './constants'
import { TotalizersItems } from './type'

export const Totalizers: React.FC<TotalizersItems> = ({
    sinteticoPorDetran,
    sinteticoPorStatus,
    sinteticoPorImagem,
    sinteticoPorTipoServico,
    sinteticoPorOrigem,
    estatistica
}) => {
    const Title: React.FC<{ title: string }> = ({ title }) => (
        <Text fontWeight="bold" fontSize="lg" mb="4" textAlign="center">
            {title}
        </Text>
    )

    const ContentWrapper: React.FC<{ children: React.ReactNode }> = ({
        children
    }) => (
        <Box
            border="2px solid #18315A"
            borderRadius="8px"
            padding="16px"
            width="280px"
            minWidth="250px"
        >
            {children}
        </Box>
    )

    const ListItem: React.FC<{ label: string; value: number }> = ({
        label,
        value
    }) => (
        <Box
            display="flex"
            gap="16px"
            alignItems="center"
            width="100%"
            justifyContent="space-between"
            padding="0 4px"
        >
            <Text fontSize="lg" noOfLines={1} title={label}>
                {label}
            </Text>
            <Text fontSize="lg" fontWeight="bold">
                {formatter.formatWithLeadingZero(value)}
            </Text>
        </Box>
    )

    const Divider: React.FC<{ margin?: string }> = ({ margin }) => (
        <Box
            height="1px"
            background="#18315A"
            margin={margin ?? '16px 0'}
            width="100%"
        />
    )

    const FlexElements: React.FC<{ children: React.ReactNode }> = ({
        children
    }) => (
        <Box display="flex" gap="16px" alignItems="center">
            {children}
        </Box>
    )

    const TotalizerSection: React.FC<{
        title: string
        data: { descricao: string; quantidadeTotal: number }[]
    }> = ({ title, data }) => (
        <ContentWrapper>
            <Title title={title} />
            <Divider />
            {data.map((item, index) => (
                <FlexElements key={`${item.descricao}-${index}`}>
                    <ListItem
                        label={item.descricao}
                        value={item.quantidadeTotal}
                    />
                </FlexElements>
            ))}
        </ContentWrapper>
    )

    const parseKeyStatistics = (key: string) => {
        const options = {
            Media: 'Média',
            'Maior Quantidade Registros': 'Maior Qtde. Rgs.',
            'Menor Quantidade Registros': 'Menor Qtde. Rgs.'
        } as any

        return options[key] || key
    }

    const chunkArray = <T,>(array: T[], size: number): T[][] => {
        return Array.from({ length: Math.ceil(array.length / size) }, (_, index) => {
            const start = index * size
            const end = start + size

            return array.slice(start, end)
        })
    }
    
    const totalizadoresDetran =
        sinteticoPorDetran.length > 0
            ? sinteticoPorDetran
            : initialStateTotalizers.sinteticoPorDetran

    const totalizadoresDetranAgrupados = chunkArray(totalizadoresDetran, 5)

    const TotalizerStatistics: React.FC = () => (
        <ContentWrapper>
            <Title title="Média de Contrato" />
            <Divider />
            {Object.entries(estatistica).map(([key, value], index) => {
                if (key === 'descricao') return null

                const label = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())

                return (
                    <FlexElements key={index}>
                        <Box
                            display="flex"
                            gap="16px"
                            alignItems="center"
                            width="100%"
                            justifyContent="space-between"
                            padding="0 4px"
                        >
                            <Text fontSize="lg" noOfLines={1} title={label}>
                                {parseKeyStatistics(label)}
                            </Text>
                            <Text fontSize="lg" fontWeight="bold">
                                {key !== 'media'
                                    ? formatter.formatWithLeadingZero(
                                          value as number
                                      )
                                    : value}
                            </Text>
                        </Box>
                    </FlexElements>
                )
            })}
        </ContentWrapper>
    )

    return (
        <Box
            margin="24px 0"
            width="100%"
            overflowX="auto"
            css={{
                '&::-webkit-scrollbar': {
                    height: '8px'
                },
                '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1'
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: '#555'
                }
            }}
        >
            <Box
                display="flex"
                gap="12px"
                minWidth="max-content"
                paddingBottom="8px"
            >
                {sinteticoPorStatus.length > 0 ? (
                    <TotalizerSection
                        title="Totalizador Status"
                        data={sinteticoPorStatus.map(item => ({
                            ...item,
                            descricao: formatter.toCamelCase(item.descricao)
                        }))}
                    />
                ) : (
                    <TotalizerSection
                        title="Totalizador Status"
                        data={initialStateTotalizers.sinteticoPorStatus}
                    />
                )}

                {sinteticoPorImagem.length > 0 ? (
                    <TotalizerSection
                        title="Totalizador Imagem"
                        data={sinteticoPorImagem}
                    />
                ) : (
                    <TotalizerSection
                        title="Totalizador Imagem"
                        data={initialStateTotalizers.sinteticoPorImagem}
                    />
                )}

                {totalizadoresDetranAgrupados.map((grupo, index) => (
                    <TotalizerSection
                        key={`totalizador-detran-${index}`}
                        title={`Totalizador Detran (${index + 1})`}
                        data={grupo}
                    />
                ))}

                {sinteticoPorTipoServico.length > 0 ? (
                    <TotalizerSection
                        title="Totalizador Serviço"
                        data={sinteticoPorTipoServico}
                    />
                ) : (
                    <TotalizerSection
                        title="Totalizador Serviço"
                        data={initialStateTotalizers.sinteticoPorTipoServico}
                    />
                )}

                {sinteticoPorOrigem.length > 0 ? (
                    <TotalizerSection
                        title="Totalizador Origem"
                        data={sinteticoPorOrigem}
                    />
                ) : (
                    <TotalizerSection
                        title="Totalizador Origem"
                        data={initialStateTotalizers.sinteticoPorOrigem}
                    />
                )}

                <TotalizerStatistics />
            </Box>
        </Box>
    )
}